import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, Stack, Typography } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import NavNeededIndicator from '@sharedCR/common/NavNeededIndicator';
import {
  getNavNeededState,
  getTabAreaIdsBySectionHref,
} from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.utils';
import PageBlock from '@sharedCR/common/PageBlock';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import { HomeIcon } from '@sharedCR/coreDesignSystem/icons';
import PendingChangeWrapperCDS from '@sharedCR/coreDesignSystem/PendingChangeWrapperCDS';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { SYSTEM_PROPERTIES } from 'constants/properties';
import useCompanyId from 'hooks/useCompanyId';
import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';
import usePopover from 'hooks/usePopover';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { selectCompanyPropertyByPropertyName } from 'store/companies/selectors';
import {
  selectIncompleteLinkedNav,
  selectPropertyAndLoanSummarySubjectPropertyData,
} from 'store/loanApplications/selectors';

import ActionMenu from '../ActionMenu';
import { ENTITY_TYPE_HREF_MAP, PROPERTY_SECTION_HREF_MAP } from '../EditModal/EditModal.constants';
import { PropertyAndLoanTestIds } from '../PropertyAndLoan.testIds';

import { getSummaryInfoConfig } from './PropertyAndLoanSummary.config';
import {
  getSummaryWrapperStyles,
  pageBlockClasses,
  summaryCardStyles,
  titleWrapperStyles,
} from './PropertyAndLoanSummary.styles';

const SUMMARY_SKELETONS_LENGTH = 4;

const PropertyAndLoanSummary = ({ isLoading, onOpenEnrichmentDrawer }) => {
  const { applicationId } = useParams();
  const companyId = useCompanyId();
  const subjectPropertyData = useSelector(selectPropertyAndLoanSummarySubjectPropertyData);
  const subjectPropertyId = subjectPropertyData?.propertyId ?? null;
  const getModifiedFieldAuthors = useModifiedFieldPendingChangeAuthors();

  const subjectPropertyEntityIds = useMemo(
    () => (subjectPropertyId ? [subjectPropertyId] : []),
    [subjectPropertyId],
  );

  const subjectPropertyCustomFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.SUBJECT_PROPERTY,
    entityIds: subjectPropertyEntityIds,
  });

  const subjectPropertyCustomFieldsPendingChangesCount = _.sum(
    Object.values(calculatePendingChanges(subjectPropertyCustomFieldsPendingChanges, true)),
  );
  const enrichmentProperty = useSelector(
    selectCompanyPropertyByPropertyName(companyId, SYSTEM_PROPERTIES.ENABLE_PROPERTY_ENRICHMENT),
  );
  const isPropertyEnrichmentEnabled = enrichmentProperty?.propertyValue === 'true';

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const navNeededByAreaId = useSelector(selectIncompleteLinkedNav());

  const propertyInfoAreaIds = useMemo(
    () => Object.values(PROPERTY_SECTION_HREF_MAP).flatMap(getTabAreaIdsBySectionHref),
    [],
  );
  const { isNeeded: isSectionNeeded, count: neededCount } = getNavNeededState(
    navNeededByAreaId,
    propertyInfoAreaIds,
  );

  const summaryInfo = useMemo(
    () => getSummaryInfoConfig(subjectPropertyData),
    [subjectPropertyData],
  );

  return (
    <PageBlock
      withAction
      actionContent={
        <Stack alignItems="center" direction="row" spacing={1}>
          {isPropertyEnrichmentEnabled && (
            <Button
              data-testid={PropertyAndLoanTestIds.PROPERTY_DETAILS_BUTTON}
              size="small"
              startIcon={<HomeIcon size={16} />}
              variant="contained"
              onClick={onOpenEnrichmentDrawer}
            >
              Property Details
            </Button>
          )}
          <MoreButtonCDS isSelected={!!anchorEl} onClick={onSetAnchorElement} />
        </Stack>
      }
      classes={pageBlockClasses}
      title={
        <Box component="span" sx={titleWrapperStyles}>
          Property Info
          {isSectionNeeded && (
            <>
              <NavNeededIndicator />
              <NavNeededCountBadge count={neededCount} />
            </>
          )}
          {!!subjectPropertyCustomFieldsPendingChangesCount && (
            <PendingChangeWrapperCDS
              isSmall
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              textSelector="span"
            >
              <Typography component="span" variant="regular12">
                {subjectPropertyCustomFieldsPendingChangesCount}{' '}
                {subjectPropertyCustomFieldsPendingChangesCount > 1
                  ? 'Pending Changes'
                  : 'Pending Change'}
              </Typography>
            </PendingChangeWrapperCDS>
          )}
        </Box>
      }
    >
      <TruncatedProvider />
      <Box
        sx={getSummaryWrapperStyles(isLoading ? SUMMARY_SKELETONS_LENGTH : summaryInfo.length)}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        {isLoading
          ? Array.from({ length: SUMMARY_SKELETONS_LENGTH }, (_unused, index) => (
              <Box key={index} sx={summaryCardStyles}>
                <DataDisplay isLoading title="" value="" variant={dataDisplayVariant} />
              </Box>
            ))
          : summaryInfo.map(
              ({
                name,
                value,
                icon,
                iconColor,
                changeKind,
                pendingChangesKey,
                noTruncate,
                withInfoIcon,
                disableInteractive,
                hoverContent,
                customTooltipStyles,
              }) => {
                const dataDisplay = (
                  <Box sx={summaryCardStyles}>
                    <DataDisplay
                      changeKind={changeKind}
                      icon={icon}
                      iconColor={iconColor}
                      isAdditionalIcon={withInfoIcon}
                      noTruncate={noTruncate}
                      pendingChangeAuthors={getModifiedFieldAuthors(
                        subjectPropertyData.modifiedFieldDetails,
                        pendingChangesKey,
                      )}
                      title={name}
                      value={value}
                      variant={dataDisplayVariant}
                    />
                  </Box>
                );

                return hoverContent ? (
                  <TooltipWrapper
                    disableInteractive={disableInteractive}
                    key={name}
                    title={hoverContent}
                    tooltipSx={customTooltipStyles}
                  >
                    {dataDisplay}
                  </TooltipWrapper>
                ) : (
                  <React.Fragment key={name}>{dataDisplay}</React.Fragment>
                );
              },
            )}
      </Box>
      <ActionMenu
        anchorEl={anchorEl}
        editText="Edit Property Info"
        entityType={ENTITY_TYPE_HREF_MAP.PROPERTY_INFO}
        onClearAnchorElement={onClearAnchorElement}
      />
    </PageBlock>
  );
};

PropertyAndLoanSummary.propTypes = {
  ...WithLoadingPropTypes,
  onOpenEnrichmentDrawer: PropTypes.func.isRequired,
};

export default PropertyAndLoanSummary;
